import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import { partnerQueryKeys } from "@/hooks/partners"
import {
  type Device,
  type DeviceSort,
  type DeviceSortField,
  Devices,
  type SortOrder,
} from "@/services/partnerApi"
import { chunkArray, waitFor } from "@/utils"
import type { DeviceSortBy, DeviceSortOrder } from "@/utils/parseDevicesSearch"

// Module scope so it is a stable constant rather than a per-call binding: the
// exhaustive-deps rule below only has to reason about values that can vary.
const QUERY_LIMIT = 200

export const deviceQueryKeys = {
  all: ["devices"] as const,
  detail: (username: string) =>
    [...deviceQueryKeys.all, "detail", username] as const,
  count: (subscriberIds?: Array<string>, sort?: DeviceSort, search?: string) =>
    [...deviceQueryKeys.all, "count", subscriberIds, sort, search] as const,
  list: (params: { offset: number; limit: number; subscriberId?: string }) =>
    [...deviceQueryKeys.all, "list", params] as const,
  page: (params: {
    page: number
    subscriberIds?: Array<string>
    usernames?: Array<string>
    sort?: DeviceSort
  }) => [...deviceQueryKeys.all, "page", params] as const,
  pages: () => [...deviceQueryKeys.all, "page"] as const,
}

// Maps the URL sort params to the generated proto DeviceSort. The generated
// DeviceSortField/SortOrder types are string unions (no runtime enum object),
// so we use the exact string literals directly rather than member access.
const SORT_FIELD_BY_SORT_BY: Record<DeviceSortBy, DeviceSortField> = {
  name: "DEVICE_SORT_FIELD_NAME",
  username: "DEVICE_SORT_FIELD_NTRIP_USERNAME",
}

const SORT_ORDER_BY_ORDER: Record<DeviceSortOrder, SortOrder> = {
  asc: "SORT_ORDER_ASC",
  desc: "SORT_ORDER_DESC",
}

const toDeviceSort = (
  sortBy: DeviceSortBy,
  order: DeviceSortOrder,
): DeviceSort => ({
  field: SORT_FIELD_BY_SORT_BY[sortBy],
  order: SORT_ORDER_BY_ORDER[order],
})

export function useDeviceIds(
  subscriberIds?: Array<string>,
  sort?: { sortBy: DeviceSortBy; order: DeviceSortOrder },
  search?: string,
  { enabled = true } = {},
) {
  const deviceSort = sort && toDeviceSort(sort.sortBy, sort.order)

  return useQuery({
    // search is part of the key (not just the request body) so that two
    // different searches -- or a search vs. no search -- are cached
    // separately rather than one clobbering the other's cached result.
    queryKey: deviceQueryKeys.count(subscriberIds, deviceSort, search),
    enabled, // Don't run until we know whether we have subscriber IDs to filter by — prevents an accidental full fetch when filtering by partner.
    staleTime: 1000 * 60 * 5,

    queryFn: async () => {
      const { devices } = await Devices.ListDeviceUsernames({
        filter_by: {
          usernames: [],
          subscriber_ids: subscriberIds ?? [],
          search_string: search,
        },
        sort: deviceSort,
      })
      return devices
    },
  })
}

type Result = {
  data: Array<Device>
  error: boolean
  isLoading: boolean
}

export function useDevicesQueries(
  subscriberIds?: Array<string>,
  ids?: Array<string>,
  sort?: { sortBy: DeviceSortBy; order: DeviceSortOrder },
) {
  const deviceSort = sort && toDeviceSort(sort.sortBy, sort.order)

  let firstQueryCompleted = false

  const queries = chunkArray(ids ?? [], QUERY_LIMIT).map((usernames, page) => {
    // firstQueryCompleted is a sequencing flag that makes pages 1..n wait on
    // page 0, not part of what identifies this query's data. Putting it in the
    // key, as the rule wants, would give the same page two cache entries and
    // refetch it when the flag flips. The plugin used to report this on the
    // queryKey property and now reports it on the returned object, so the
    // directive moved out here with it.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return {
      queryKey: deviceQueryKeys.page({
        page,
        subscriberIds,
        usernames,
        sort: deviceSort,
      }),
      queryFn: async () => {
        if (page !== 0 && !firstQueryCompleted) {
          await waitFor(() => firstQueryCompleted)
        }
        const { devices } = await Devices.ListDevices({
          filter_by: {
            usernames,
            subscriber_ids: subscriberIds ?? [],
          },
          pagination: { offset: 0, limit: QUERY_LIMIT },
          sort: deviceSort,
        })
        if (page === 0) {
          firstQueryCompleted = true
        }
        return devices
      },
      staleTime: 1000 * 60 * 5,
    }
  })

  return useQueries({
    queries,
    combine: (results) =>
      results.reduce<Result>(
        (acc, result) => {
          return {
            data: [
              ...acc.data,
              ...(Array.isArray(result.data) ? result.data : [result.data]),
            ].filter((d): d is Device => d !== undefined),
            error: acc.error || !!result.error,
            isLoading: acc.isLoading || result.isLoading,
          }
        },
        {
          data: [],
          error: false,
          isLoading: false,
        },
      ),
  })
}

export function useDevices({
  offset,
  limit,
  subscriberId,
}: {
  offset: number
  limit: number
  subscriberId?: string
}) {
  return useQuery({
    queryKey: deviceQueryKeys.list({ offset, limit, subscriberId }),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const response = await Devices.ListDevices({
        filter_by: {
          usernames: [],
          subscriber_ids: subscriberId ? [subscriberId] : [],
        },
        pagination: { offset, limit },
      })
      return response.devices
    },
  })
}

export function useDevice(username: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: deviceQueryKeys.detail(username),

    staleTime: 1000 * 60 * 5,

    // The generated response types this field optional. Returning it straight
    // through means handing react-query an undefined, which it rejects with
    // "Query data cannot be undefined" and no clue which record was missing.
    queryFn: async () => {
      const { device } = await Devices.GetDevice({ username })
      if (!device) {
        throw new Error(`No device returned for username ${username}`)
      }
      return device
    },

    initialData: () =>
      queryClient
        .getQueriesData<Array<Device>>({
          queryKey: deviceQueryKeys.pages(),
        })
        .flatMap(([_queryKey, devices]) => devices)
        .find((d) => d?.username === username),

    initialDataUpdatedAt: () =>
      queryClient.getQueryState(deviceQueryKeys.all)?.dataUpdatedAt,
  })
}

type CreateRequest = Device & {
  subscriber_id: string
  password?: string
  expires_after?: string
}

type UpdateRequest = Device & {
  password?: string
}

export function useCreateDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (device: CreateRequest) => {
      if (device.registered) {
        await Devices.CreateRegisteredDevice({
          allow_notifications: device.allow_notifications,
          comment: device.comment,
          email: device.email,
          enabled: device.enabled ?? true,
          expires_at: device.expires_at,
          name: device.name,
          password: device.password,
          skylark_type: device.skylark_type ?? "SKYLARK_CX",
          subscriber_id: device.subscriber_id,
          username: device.username,
        })
      } else if (device.pre_provisioned) {
        await Devices.CreatePreProvisionedDevice({
          allow_notifications: device.allow_notifications,
          comment: device.comment,
          email: device.email,
          enabled: device.enabled ?? false,
          expires_after: device.pre_provisioned.expires_after,
          name: device.name,
          password: device.password,
          skylark_type: device.skylark_type ?? "SKYLARK_CX",
          subscriber_id: device.subscriber_id,
          username: device.username,
        })
      } else {
        throw new Error("Invalid device type")
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all })
      queryClient.invalidateQueries({ queryKey: partnerQueryKeys.all })
    },
  })
}

export function useUpdateDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (device: UpdateRequest) => {
      if (device.registered) {
        await Devices.UpdateRegisteredDevice({
          allow_notifications: device.allow_notifications,
          comment: device.comment,
          email: device.email,
          enabled: device.enabled,
          expires_at: device.expires_at,
          name: device.name,
          password: device.password,
          skylark_type: device.skylark_type,
          username: device.username,
        })
      } else if (device.pre_provisioned) {
        await Devices.UpdatePreProvisionedDevice({
          activated: device.pre_provisioned.activated,
          allow_notifications: device.allow_notifications,
          comment: device.comment,
          email: device.email,
          enabled: device.enabled,
          expires_after: device.pre_provisioned.expires_after,
          expires_at: device.expires_at,
          name: device.name,
          password: device.password,
          skylark_type: device.skylark_type,
          username: device.username,
        })
      } else if (device.auto_registered) {
        await Devices.UpdateAutoRegisteredDevice({
          comment: device.comment,
          enabled: device.enabled,
          expires_at: device.expires_at,
          name: device.name,
          skylark_type: device.skylark_type,
          username: device.username,
        })
      } else {
        throw new Error("Invalid device type")
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all })
    },
  })
}

export function useDeleteDevice() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: Devices.DeleteDevice,
    onMutate: (device: Device) => {
      // first remove the detail query, then invalidate any other queries that may be affected
      queryClient.removeQueries({
        queryKey: deviceQueryKeys.detail(device.username),
      })
      queryClient.invalidateQueries({
        // Prefix-match, not deviceQueryKeys.count(...): count queryKeys now
        // carry a `sort` element, and this invalidation doesn't know the
        // current sort. React Query prefix-matches, so omitting the trailing
        // sort element still matches every count query regardless of sort.
        queryKey: [
          ...deviceQueryKeys.all,
          "count",
          device.subscriber_id ? [device.subscriber_id] : undefined,
        ],
      })
    },
    onSettled: () => {
      // invalite queries that may be affected
      queryClient.invalidateQueries({ queryKey: deviceQueryKeys.all })
      queryClient.invalidateQueries({ queryKey: partnerQueryKeys.all })
    },
  })
}
